import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from 'vue';
import { useStore } from "@/hooks/useStore";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import TrainingVideosRepository from "@/utils/API/TrainingVideoRepository";
import VModal from "@/components/vModal.vue";
import {useNotify} from "@/hooks/useNotify";
import VGenericConfirmationModal from "@/components/vGenericConfirmationModal.vue";
import CollapsableCardWithHeader from "@/components/bootstrap/CollapsableCardWithHeader.vue";

interface Video {
  id: number;
  name: string;
  categoryId: number;
  tutorialLink: string;
  description: string;
  youtubeLink: string;
  sortOrder?: number;
}

interface Category {
  id: number;
  name: string;
  sortOrder?: number;
  videos: Video[];
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TrainingVideos',
  setup(__props) {

const categories = ref<Category[]>([]);
const store = useStore();
const notify = useNotify();
const defaultThumbnail = "/img/apple-profile.fd379455.jpg";
const viewMode = ref<'grid' | 'list'>('grid');

const showCategoryModal = ref(false);
const showVideoModal = ref(false);

const isEditingCategory = ref(false);
const isEditingVideo = ref(false);

const showDeleteConfirmModal = ref(false);
const deleteTargetType = ref<'category' | 'video' | null>(null);
const categoryPendingDelete = ref<Category | null>(null);
const videoPendingDelete = ref<Video | null>(null);

// Video Modal
const showYoutubeModal = ref(false);
const selectedYoutubeUrl = ref("");
const selectedYoutubeTitle = ref("");

const categoryForm = ref({
  id: 0,
  name: ""
});

const openManageCategoryId = ref<number | null>(null);
const videoForm = ref<Video>({
  id: 0,
  name: "",
  categoryId: 0,
  tutorialLink: "",
  description: "",
  youtubeLink: ""
});

const flatCategories = computed(() => categories.value.map(({ id, name, sortOrder }) => ({
  id,
  name,
  sortOrder
})));

const isSupademo = (link: string) => {
  return !!link && link.includes("supademo.com");
};

const getThumbnail = (video: Video) => {
  if (video.youtubeLink) {
    const match = video.youtubeLink.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/
    );

    if (match) {
      return `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`;
    }
  }

  return defaultThumbnail;
};

async function loadData() {
  const { data: categoryData } = await TrainingVideosRepository.listCategories();
  const { data: videoData } = await TrainingVideosRepository.listVideos();

  categories.value = categoryData
    .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
    .map((category: any) => ({
      ...category,
      videos: videoData
        .filter((v: any) => v.categoryId === category.id)
        .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
    }));
}

function resetCategoryForm() {
  categoryForm.value = {
    id: 0,
    name: ""
  };
}

function resetVideoForm() {
  videoForm.value = {
    id: 0,
    name: "",
    categoryId: flatCategories.value[0]?.id || 0,
    tutorialLink: "",
    description: "",
    youtubeLink: ""
  };
}

function openCreateCategoryModal() {
  resetCategoryForm();
  isEditingCategory.value = false;
  showCategoryModal.value = true;
}

function openEditCategoryModal(category: Category) {
  categoryForm.value = {
    id: category.id,
    name: category.name
  };
  isEditingCategory.value = true;
  showCategoryModal.value = true;
}

function closeCategoryModal() {
  showCategoryModal.value = false;
  resetCategoryForm();
}

function toggleManageMenu(categoryId: number) {
  openManageCategoryId.value =
    openManageCategoryId.value === categoryId ? null : categoryId;
}

function closeManageMenu() {
  openManageCategoryId.value = null;
}

async function saveCategory() {
  if (!categoryForm.value.name.trim()) return;

  if (isEditingCategory.value) {
    await TrainingVideosRepository.updateCategory(
      categoryForm.value.id,
      categoryForm.value.name
    );
  }
  else {
    await TrainingVideosRepository.createCategory(categoryForm.value.name);
  }

  closeCategoryModal();
  await loadData();
}

function deleteCategory(category: Category) {
  categoryPendingDelete.value = category;
  videoPendingDelete.value = null;
  deleteTargetType.value = 'category';
  showDeleteConfirmModal.value = true;
}

function openCreateVideoModal(categoryId?: number) {
  resetVideoForm();
  videoForm.value.categoryId = categoryId || flatCategories.value[0]?.id || 0;
  isEditingVideo.value = false;
  showVideoModal.value = true;
}

function openEditVideoModal(video: Video) {
  videoForm.value = {
    id: video.id,
    name: video.name,
    categoryId: video.categoryId,
    tutorialLink: video.tutorialLink || "",
    description: video.description || "",
    youtubeLink: video.youtubeLink || ""
  };
  isEditingVideo.value = true;
  showVideoModal.value = true;
}

function closeVideoModal() {
  showVideoModal.value = false;
  resetVideoForm();
}

async function saveVideo() {
  if (!videoForm.value.name.trim() || !videoForm.value.categoryId) return;

  if (isEditingVideo.value) {
    await TrainingVideosRepository.updateVideo(videoForm.value);
  }
  else {
    await TrainingVideosRepository.createVideo(videoForm.value);
  }

  closeVideoModal();
  await loadData();
}

function deleteVideo(video: Video) {
  videoPendingDelete.value = video;
  categoryPendingDelete.value = null;
  deleteTargetType.value = 'video';
  showDeleteConfirmModal.value = true;
}

function closeDeleteConfirmModal() {
  showDeleteConfirmModal.value = false;
  deleteTargetType.value = null;
  categoryPendingDelete.value = null;
  videoPendingDelete.value = null;
}

async function confirmDelete() {
  try {
    if (deleteTargetType.value === 'category' && categoryPendingDelete.value) {
      await TrainingVideosRepository.deleteCategory(categoryPendingDelete.value.id);
    }

    if (deleteTargetType.value === 'video' && videoPendingDelete.value) {
      await TrainingVideosRepository.deleteVideo(videoPendingDelete.value.id);
    }

    closeDeleteConfirmModal();
    await loadData();
  }
  catch {
    notify.error("This category cannot be deleted because it still has videos.");
    closeDeleteConfirmModal();
  }
}

function openYoutubeModal(video: Video) {
  if (!video.youtubeLink) return;

  selectedYoutubeUrl.value = video.youtubeLink;
  selectedYoutubeTitle.value = video.name;
  showYoutubeModal.value = true;
}

function closeYoutubeModal() {
  showYoutubeModal.value = false;
  selectedYoutubeUrl.value = "";
  selectedYoutubeTitle.value = "";
}

function getYoutubeEmbedUrl(url: string) {
  if (!url) return "";

  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&]+)/,
    /(?:youtu\.be\/)([^?&]+)/,
    /(?:youtube\.com\/shorts\/)([^?&]+)/
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://www.youtube.com/embed/${match[1]}?autoplay=1`;
    }
  }

  return "";
}

onMounted(async() => {
  await loadData();

  await store.value.dispatch(TRANSLATIONS_FETCH, {
    category: "pages",
    collections: ["common", "ingredients", "ingredient cost"],
  });
});

return { __sfc: true,categories, store, notify, defaultThumbnail, viewMode, showCategoryModal, showVideoModal, isEditingCategory, isEditingVideo, showDeleteConfirmModal, deleteTargetType, categoryPendingDelete, videoPendingDelete, showYoutubeModal, selectedYoutubeUrl, selectedYoutubeTitle, categoryForm, openManageCategoryId, videoForm, flatCategories, isSupademo, getThumbnail, loadData, resetCategoryForm, resetVideoForm, openCreateCategoryModal, openEditCategoryModal, closeCategoryModal, toggleManageMenu, closeManageMenu, saveCategory, deleteCategory, openCreateVideoModal, openEditVideoModal, closeVideoModal, saveVideo, deleteVideo, closeDeleteConfirmModal, confirmDelete, openYoutubeModal, closeYoutubeModal, getYoutubeEmbedUrl, VModal, VGenericConfirmationModal, CollapsableCardWithHeader }
}

})